import { Modal } from "@ews-admin/global-design-system";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { paymentApi } from "@/services/payment.api";
import {
  OpenPaymentIntent,
  PAYMENT_SESSION_KEY,
  PaymentSessionContext,
} from "@/types/payment";

import {
  OPEN_PAYMENT_POLL_MAX_ATTEMPTS,
  openPaymentPollDelayMs,
} from "./polling";

export type OpenPaymentOutcome = "completed" | "cancelled" | "failed";

interface PaymentInProgressOverlayProps {
  invoiceId: number;
  intent: OpenPaymentIntent;
  /** Called once the open payment is over; the parent then unmounts the overlay. */
  onResolved: (outcome: OpenPaymentOutcome) => void;
}

// MED-1321: the overlay is deliberately not dismissible — Escape, the backdrop
// and the Modal's built-in close button all land here and do nothing. The
// manager has to resume or cancel the open checkout, so a second checkout can
// never be started for the same invoice.
const ignoreClose = () => {};

// Unknown or foreign intent. Matched on the error code only: the axios
// interceptor rejects with the BFF error body, not the HTTP status (404).
const isIntentNotFound = (error: unknown) =>
  (error as { code?: string } | undefined)?.code === "PAYMENT_INTENT_NOT_FOUND";

export const PaymentInProgressOverlay: React.FC<PaymentInProgressOverlayProps> = ({
  invoiceId,
  intent,
  onResolved,
}) => {
  const { t, i18n } = useTranslation();
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // Polling gave up after MAX_ATTEMPTS without a terminal status (MED-1430).
  const [unconfirmed, setUnconfirmed] = useState(false);
  // Polling only runs while the tab is in the foreground: a hidden tab cannot
  // show the manager anything, so its requests are pure cost.
  const [documentVisible, setDocumentVisible] = useState(
    () => typeof document === "undefined" || !document.hidden,
  );
  // Survives the pause/resume of the polling effect, so hiding and re-showing the
  // tab does not reset the backoff to 5 s and start the budget again.
  const attempts = useRef(0);
  const resolvedRef = useRef(false);
  // Latest callbacks, read from the polling loop without restarting it on re-render.
  const latestRef = useRef({ t, onResolved });
  latestRef.current = { t, onResolved };

  const finish = useCallback((outcome: OpenPaymentOutcome, toastKey?: string) => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    // The checkout is over — don't leave its context behind for /payment/callback.
    sessionStorage.removeItem(PAYMENT_SESSION_KEY);
    if (toastKey) toast.success(latestRef.current.t(toastKey));
    latestRef.current.onResolved(outcome);
  }, []);

  // Pause polling while the tab is hidden, resume when it comes back. The effect
  // below depends on documentVisible, so flipping it tears the timer down and
  // builds it back up without resetting the attempt count.
  useEffect(() => {
    const onVisibilityChange = () => setDocumentVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  useEffect(() => {
    if (unconfirmed || !documentVisible) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const schedule = () => {
      if (!active) return;
      if (attempts.current >= OPEN_PAYMENT_POLL_MAX_ATTEMPTS) {
        // Give up rather than poll a payment that is never going to resolve. The
        // manager gets an explanation and a button, not a silent stop.
        setUnconfirmed(true);
        return;
      }
      timer = setTimeout(() => void poll(), openPaymentPollDelayMs(attempts.current));
    };

    const poll = async () => {
      // Counted here, not in schedule(): an attempt cancelled by the tab being
      // hidden never reached the server, so it must not spend the budget.
      attempts.current += 1;
      try {
        const { status } = await paymentApi.getInvoicePaymentStatus(invoiceId, intent.paymentIntentId);
        if (!active) return;
        if (status === "COMPLETED") {
          finish("completed", "payment.callback.success.title");
          return;
        }
        if (status === "CANCELLED" || status === "FAILED") {
          finish(status === "FAILED" ? "failed" : "cancelled");
          return;
        }
      } catch (error) {
        if (!active) return;
        // The intent no longer exists server-side: there is nothing left to wait for.
        if (isIntentNotFound(error)) {
          finish("cancelled");
          return;
        }
        // Transient error: keep polling, but it still costs an attempt — an
        // endpoint that is down must not be retried forever either.
      }
      schedule();
    };

    schedule();

    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [invoiceId, intent.paymentIntentId, finish, unconfirmed, documentVisible]);

  // Manual retry from the unconfirmed state: fresh budget, poll again.
  const checkAgain = () => {
    attempts.current = 0;
    setUnconfirmed(false);
  };

  const handleResume = () => {
    const ctx: PaymentSessionContext = {
      type: "facility",
      invoiceId,
      paymentIntentId: intent.paymentIntentId,
    };
    sessionStorage.setItem(PAYMENT_SESSION_KEY, JSON.stringify(ctx));
    window.location.assign(intent.checkoutUrl);
  };

  const handleConfirmCancel = async () => {
    setCancelling(true);
    try {
      const { status } = await paymentApi.cancelInvoicePayment(invoiceId, intent.paymentIntentId);
      // The manager may have finished paying on PayDunya in the meantime.
      if (status === "COMPLETED") {
        finish("completed", "payment.callback.success.title");
      } else {
        finish("cancelled", "payment.inProgress.cancelled");
      }
    } catch (error) {
      // The intent no longer exists server-side: there is nothing left to cancel.
      if (isIntentNotFound(error)) {
        finish("cancelled");
        return;
      }
      // Other failures are already surfaced by the global error modal (axios
      // interceptor); keep the overlay so the manager can retry or resume.
      setCancelling(false);
      setConfirmingCancel(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={ignoreClose}
      closeOnOverlayClick={false}
      // The Modal's built-in X cannot close this overlay, so don't show it.
      className="[&_button[aria-label^=Close]]:hidden"
      variant={confirmingCancel ? "error" : "warning"}
      size="md"
      title={
        confirmingCancel
          ? t("payment.inProgress.cancelConfirmTitle")
          : unconfirmed
            ? t("payment.inProgress.unconfirmedTitle")
            : t("payment.inProgress.title")
      }
      primaryAction={confirmingCancel ? t("payment.inProgress.cancelConfirmCta") : t("payment.inProgress.resume")}
      onPrimaryAction={confirmingCancel ? () => void handleConfirmCancel() : handleResume}
      secondaryAction={confirmingCancel ? t("payment.inProgress.keepPaying") : t("payment.inProgress.cancel")}
      onSecondaryAction={() => setConfirmingCancel(!confirmingCancel)}
      isLoading={cancelling}
    >
      {confirmingCancel ? (
        <p className="text-sm leading-relaxed text-neutral-600">
          {t("payment.inProgress.cancelConfirmMessage")}
        </p>
      ) : (
        <div className="space-y-4">
          <p
            className="text-sm leading-relaxed text-neutral-600"
            data-testid="payment-in-progress-message"
          >
            {unconfirmed
              ? t("payment.inProgress.unconfirmedMessage")
              : t("payment.inProgress.message")}
          </p>
          {unconfirmed && (
            <button
              type="button"
              onClick={checkAgain}
              data-testid="payment-in-progress-check-again"
              className="text-sm font-medium underline text-primary-600 hover:text-primary-700"
            >
              {t("payment.inProgress.checkAgain")}
            </button>
          )}
          <div className="p-4 rounded-xl bg-neutral-50">
            <p className="text-xs text-neutral-500">{t("payment.invoice.amount")}</p>
            <p className="text-lg font-bold text-primary-600">
              {intent.amount.toLocaleString(i18n.language)} {intent.currency}
            </p>
          </div>
        </div>
      )}
    </Modal>
  );
};
